import { useEffect, useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import { Card } from '@actual-app/components/card';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import { getFinancialErrorCode } from '#components/product-finance/errors';

export function StepCard({
  title,
  children,
}: {
  title: ReactNode;
  children: ReactNode;
}) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <Card style={{ margin: 0 }}>
      <View style={{ padding: 20, gap: 14 }}>
        <h2 ref={headingRef} tabIndex={-1} style={{ margin: 0, fontSize: 20 }}>
          {title}
        </h2>
        {children}
      </View>
    </Card>
  );
}

export function Field({
  label,
  htmlFor,
  children,
}: {
  label: ReactNode;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} style={{ display: 'grid', gap: 5 }}>
      <strong>{label}</strong>
      {children}
    </label>
  );
}

export function SourceList({
  empty,
  items,
}: {
  empty: ReactNode;
  items: readonly Readonly<{ id: string; name: string; detail: string }>[];
}) {
  if (items.length === 0) {
    return <p style={helpStyle}>{empty}</p>;
  }
  return (
    <ul style={{ margin: 0, paddingLeft: 22 }}>
      {items.map(item => (
        <li key={item.id} style={{ marginBottom: 6 }}>
          <strong>{item.name}</strong>
          <span style={{ color: theme.pageTextSubdued }}> — {item.detail}</span>
        </li>
      ))}
    </ul>
  );
}

export function FormError({ message }: { message: string | null }) {
  return message === null ? null : (
    <p role="alert" style={{ margin: 0, color: theme.errorText }}>
      {message}
    </p>
  );
}

export function formatMoneyForInput(minorUnits: number): string {
  return `${Math.floor(minorUnits / 100)}.${String(minorUnits % 100).padStart(2, '0')}`;
}

export function getFinancialErrorMessage(
  error: unknown,
  t: (message: string) => string,
): string {
  switch (getFinancialErrorCode(error)) {
    case 'currency-change-blocked':
      return t('Currency cannot be changed after financial data exists.');
    case 'invalid-timezone':
      return t('Choose a valid IANA time zone.');
    case 'invalid-anchor':
      return t('Choose an existing fixed income as the cycle anchor.');
    case 'source-already-exists':
      return t('That financial source already exists. Try again.');
    default:
      return t(
        'We could not save this step. Your existing data was not changed.',
      );
  }
}

export function assertNever(value: never): never {
  throw new Error(`Unexpected onboarding value: ${String(value)}`);
}

export const formStyle: CSSProperties = {
  display: 'grid',
  gap: 14,
};

export const inputStyle: CSSProperties = {
  width: '100%',
  minHeight: 42,
  boxSizing: 'border-box',
  border: `1px solid ${theme.formInputBorder}`,
  borderRadius: 5,
  padding: '8px 10px',
  color: theme.formInputText,
  background: theme.tableBackground,
  font: 'inherit',
};

export const helpStyle: CSSProperties = {
  margin: 0,
  color: theme.pageTextSubdued,
  lineHeight: 1.45,
};

export const checkboxStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minHeight: 42,
};

export const accountOptionStyle: CSSProperties = {
  ...checkboxStyle,
  padding: '8px 10px',
  borderRadius: 6,
  background: theme.pageBackground,
};

export const actionStyle: CSSProperties = {
  minHeight: 42,
  alignSelf: 'flex-start',
};
